/**
 * Metric Catalog — the unified, queryable description of every metric
 * the product can chart, across all data domains.
 *
 * WHY THIS EXISTS
 * ---------------
 * The original Explore surface (`metric-explore.ts`) was built when the
 * only data source was synthetic Lighthouse (`snapshots_events`). Since
 * then the product gained real-user CWV, traffic KPIs, breakdowns, and
 * soon conversion + behaviour. A hand-configured series-builder doesn't
 * scale to that much data for a non-technical audience.
 *
 * The catalog is the foundation for a different model: the user asks
 * for what they want in natural language, an assistant translates that
 * into a `MetricQuerySpec` (or a whole `DashboardSpec`), and a resolver
 * maps each spec to the right Tinybird pipe. The **spec is an internal
 * representation** — users never see or author it directly; it's how
 * the rendering + pinning works underneath.
 *
 * So this file is consumed three ways:
 *   1. The assistant grounds NL → spec generation against this registry
 *      (it can only reference metrics + dimensions that exist here).
 *   2. A resolver (apps/api) maps `{ source, metricId, aggregation }`
 *      onto the concrete pipe call.
 *   3. The renderer (dashboard) formats values + picks bands from the
 *      definition (unit, lowerIsBetter, thresholds).
 *
 * Keep it data + types + pure helpers only — no React, no fetch. It's
 * shared by API, dashboard, and the CLI/tools generator.
 */

// ─── Domains + sources ──────────────────────────────────────────────────────

/**
 * Top-level lens a metric belongs to. Mirrors the dashboard's primary
 * nav (Performance / Traffic / Behaviours / Sales) so the assistant
 * can reason about "show me performance" vs "show me sales" without a
 * separate mapping.
 */
export const METRIC_DOMAINS = ['performance', 'traffic', 'behaviour', 'sales'] as const;
export type MetricDomain = (typeof METRIC_DOMAINS)[number];

/**
 * Which datasource + pipe family backs a metric. The resolver switches
 * on this to pick the concrete Tinybird pipe. Adding a new backing
 * pipe is a new `source` value + a resolver branch — the catalog entry
 * stays declarative.
 */
export const METRIC_SOURCES = [
  /** Synthetic Lighthouse audits — `snapshots_events`, the existing
   *  Explore datasource. p50/p75/p95 over arbitrary buckets. */
  'synthetic',
  /** Real-user Core Web Vitals — `pixel_site_perf*` pipes. */
  'rum_perf',
  /** Real-user traffic KPIs — `pixel_site_kpis*` / `pixel_site_trend`. */
  'rum_traffic',
  /** Real-user events (clicks, scroll, custom) — `pixel_*_events*`.
   *  Backs the Behaviours + Sales domains; pipes land later. */
  'rum_events',
] as const;
export type MetricSource = (typeof METRIC_SOURCES)[number];

// ─── Units + aggregations ─────────────────────────────────────────────────────

/**
 * Display unit — drives value formatting, Y-axis assignment, and which
 * tooltip/format helper the renderer uses. Superset of the old
 * `MetricUnit` in metric-explore.ts (which only had ms/score/unitless).
 */
export const METRIC_UNITS = [
  'ms',
  'score', // 0..100 Lighthouse category score
  'count', // integer tallies (visitors, sessions, pageviews)
  'ratio', // unbounded ratio (pages per session)
  'percent', // 0..1 rendered as % (bounce rate, good-density)
  'bytes',
  'currency',
  'cls', // unitless layout-shift score, formatted to 2dp
] as const;
export type CatalogMetricUnit = (typeof METRIC_UNITS)[number];

/**
 * How a metric collapses many samples into one number. Not every
 * aggregation is valid for every metric (you don't p75 a visitor
 * count), so each definition declares its allowed set.
 */
export const AGGREGATIONS = ['p50', 'p75', 'p95', 'p99', 'avg', 'sum', 'count', 'rate'] as const;
export type Aggregation = (typeof AGGREGATIONS)[number];

// ─── Dimensions ───────────────────────────────────────────────────────────────

/**
 * Slices a metric can be filtered or grouped by. Maps to a column on
 * the underlying datasource; the resolver translates the friendly name
 * to the real column (e.g. `device` → `form_factor`). Each definition
 * lists only the dimensions its source actually carries.
 */
export const METRIC_DIMENSIONS = [
  'device', // phone / tablet / desktop (form_factor)
  'country', // ISO 3166-1 alpha-2
  'region', // sub-country region code
  'connection', // network_type bucket (fast / slower / unknown)
  'browser', // browser_family
  'os', // os_family
  'page', // page_path / page_uuid
  'source', // referrer_host / utm_source
  'channel', // direct / organic / social / …
  'campaign', // utm_campaign
] as const;
export type MetricDimension = (typeof METRIC_DIMENSIONS)[number];

// ─── Catalog entry ──────────────────────────────────────────────────────────

export interface MetricDefinition {
  /** Stable identifier the spec references. Namespaced by source so
   *  synthetic LCP and real-user LCP don't collide
   *  (`synthetic.lcp` vs `rum.lcp`). */
  id: string;
  /** Human label for chips, legends, tooltips. */
  label: string;
  /** One-line description — also fed to the assistant so it can match
   *  fuzzy NL ("how fast do real users load") to the right metric. */
  description: string;
  domain: MetricDomain;
  source: MetricSource;
  unit: CatalogMetricUnit;
  /** Direction of "good". Drives ranking sort + band tinting.
   *  Visitors/revenue are higher-is-better; CWV are lower-is-better. */
  lowerIsBetter: boolean;
  /** The aggregation used when the spec doesn't name one. */
  defaultAggregation: Aggregation;
  /** Aggregations the resolver supports for this metric. */
  allowedAggregations: readonly Aggregation[];
  /** Dimensions this metric can be **filtered** by (equality filters
   *  the backing pipe accepts). */
  dimensions: readonly MetricDimension[];
  /** Dimensions this metric can be **broken down** by (groupBy). A
   *  subset of `dimensions` that the resolver actually backs with a
   *  breakdown pipe — real-user CWV only by country, ratio metrics
   *  (bounce / pages-per-session / duration) not at all. Kept separate
   *  so the assistant can't emit a breakdown the resolver would reject
   *  (the catalog/resolver mismatch that left "bounce rate by source"
   *  promoted but unsupported). */
  groupByDimensions: readonly MetricDimension[];
  /** Optional good/poor thresholds (CWV) — the renderer's band logic
   *  reads these instead of hardcoding per metric. Omitted for metrics
   *  with no notion of pass/fail (counts). */
  thresholds?: { good: number; poor: number };
  /** When true, the metric is declared but not yet backed by a pipe.
   *  The assistant can mention it ("conversion data isn't connected
   *  yet") but won't emit a spec that queries it. */
  comingSoon?: boolean;
}

// ─── The registry ─────────────────────────────────────────────────────────────
//
// Grouped by domain for readability. IDs are `<source-prefix>.<metric>`
// so the same underlying metric measured two ways (synthetic vs real-
// user LCP) gets distinct, self-describing ids.

// Declared here (not in the builders block below) so it's initialised
// before the registry arrays that reference it via `rumCwv` — see the
// TDZ note above the builders.
const RUM_PERF_DIMENSIONS: readonly MetricDimension[] = [
  'device',
  'country',
  'region',
  'connection',
  'browser',
  'os',
  'page',
];

const PERFORMANCE_METRICS: MetricDefinition[] = [
  // Real-user CWV (rum_perf) — the headline performance story.
  rumCwv('rum.lcp', 'LCP (real users)', 'Largest Contentful Paint from real visitors', 'ms', {
    good: 2500,
    poor: 4000,
  }),
  rumCwv('rum.inp', 'INP (real users)', 'Interaction to Next Paint from real visitors', 'ms', {
    good: 200,
    poor: 500,
  }),
  rumCwv('rum.cls', 'CLS (real users)', 'Cumulative Layout Shift from real visitors', 'cls', {
    good: 0.1,
    poor: 0.25,
  }),
  rumCwv('rum.ttfb', 'TTFB (real users)', 'Time to First Byte from real visitors', 'ms', {
    good: 800,
    poor: 1800,
  }),
  rumCwv('rum.fcp', 'FCP (real users)', 'First Contentful Paint from real visitors', 'ms', {
    good: 1800,
    poor: 3000,
  }),
  // Synthetic Lighthouse — lab measurements, comparable across runs.
  synthetic('synthetic.lcp', 'LCP (lab)', 'Largest Contentful Paint from synthetic audits', 'ms', {
    good: 2500,
    poor: 4000,
  }),
  synthetic('synthetic.cls', 'CLS (lab)', 'Cumulative Layout Shift from synthetic audits', 'cls', {
    good: 0.1,
    poor: 0.25,
  }),
  synthetic('synthetic.tbt', 'TBT (lab)', 'Total Blocking Time from synthetic audits', 'ms', {
    good: 200,
    poor: 600,
  }),
  synthetic('synthetic.fcp', 'FCP (lab)', 'First Contentful Paint from synthetic audits', 'ms', {
    good: 1800,
    poor: 3000,
  }),
  synthetic('synthetic.si', 'Speed Index (lab)', 'Speed Index from synthetic audits', 'ms', {
    good: 3400,
    poor: 5800,
  }),
  syntheticScore(
    'synthetic.performance_score',
    'Performance score',
    'Lighthouse performance score',
  ),
  syntheticScore(
    'synthetic.accessibility_score',
    'Accessibility score',
    'Lighthouse accessibility score',
  ),
  syntheticScore(
    'synthetic.best_practices_score',
    'Best Practices score',
    'Lighthouse best-practices score',
  ),
  syntheticScore('synthetic.seo_score', 'SEO score', 'Lighthouse SEO score'),
];

const TRAFFIC_METRICS: MetricDefinition[] = [
  trafficCount('traffic.visitors', 'Unique visitors', 'Distinct people in the window'),
  trafficCount('traffic.sessions', 'Sessions', 'Visit-sized engagements'),
  trafficCount('traffic.pageviews', 'Pageviews', 'Total page renders'),
  {
    id: 'traffic.pages_per_session',
    label: 'Pages / session',
    description: 'Average pages viewed per visit',
    domain: 'traffic',
    source: 'rum_traffic',
    unit: 'ratio',
    lowerIsBetter: false,
    defaultAggregation: 'avg',
    allowedAggregations: ['avg'],
    dimensions: ['device', 'country', 'channel', 'source'],
    // Ratio metric — the breakdown pipe has no per-bucket column for
    // it, so it's a time series only (resolver rejects any groupBy).
    groupByDimensions: [],
  },
  {
    id: 'traffic.bounce_rate',
    label: 'Bounce rate',
    description: 'Share of single-page sessions',
    domain: 'traffic',
    source: 'rum_traffic',
    unit: 'percent',
    lowerIsBetter: true,
    defaultAggregation: 'rate',
    allowedAggregations: ['rate'],
    dimensions: ['device', 'country', 'channel', 'source', 'page'],
    // Ratio metric — time series only (no per-bucket breakdown pipe).
    groupByDimensions: [],
  },
  {
    id: 'traffic.session_duration',
    label: 'Session duration',
    description: 'Average engaged time per visit',
    domain: 'traffic',
    source: 'rum_traffic',
    unit: 'ms',
    lowerIsBetter: false,
    defaultAggregation: 'avg',
    allowedAggregations: ['avg'],
    dimensions: ['device', 'country', 'channel', 'source'],
    // Ratio metric — time series only (no per-bucket breakdown pipe).
    groupByDimensions: [],
  },
];

// Behaviour + Sales — backed by the real-user event stream
// (pixel_events_short / _long) via the rum_events resolver branch.
// Sales events follow a convention within the existing event schema
// (event_kind='conversion', revenue in event_props.value); dev data is
// seeded so the cards render real values while the tracking interface
// is designed.
const BEHAVIOUR_METRICS: MetricDefinition[] = [
  {
    id: 'behaviour.outbound_clicks',
    label: 'Outbound clicks',
    description: 'Clicks on links leaving the site',
    domain: 'behaviour',
    source: 'rum_events',
    unit: 'count',
    lowerIsBetter: false,
    defaultAggregation: 'sum',
    allowedAggregations: ['sum'],
    dimensions: ['page', 'device', 'country', 'source', 'channel', 'browser', 'os'],
    groupByDimensions: ['page', 'device', 'country', 'source'],
  },
  {
    id: 'behaviour.scroll_depth',
    label: 'Scroll depth',
    description: 'How far down the page visitors scroll (p75)',
    domain: 'behaviour',
    source: 'rum_events',
    unit: 'percent',
    lowerIsBetter: false,
    defaultAggregation: 'p75',
    allowedAggregations: ['p50', 'p75'],
    dimensions: ['page', 'device', 'country'],
    groupByDimensions: ['page', 'device'],
  },
];

const SALES_METRICS: MetricDefinition[] = [
  {
    id: 'sales.revenue',
    label: 'Revenue',
    description: 'Total revenue from tracked conversion events',
    domain: 'sales',
    source: 'rum_events',
    unit: 'currency',
    lowerIsBetter: false,
    defaultAggregation: 'sum',
    allowedAggregations: ['sum'],
    dimensions: ['source', 'channel', 'campaign', 'country', 'device', 'page'],
    groupByDimensions: ['source', 'channel', 'campaign', 'country', 'device'],
  },
  {
    id: 'sales.conversion_rate',
    label: 'Conversion rate',
    description: 'Share of sessions that converted',
    domain: 'sales',
    source: 'rum_events',
    unit: 'percent',
    lowerIsBetter: false,
    defaultAggregation: 'rate',
    allowedAggregations: ['rate'],
    dimensions: ['source', 'channel', 'campaign', 'country', 'device'],
    groupByDimensions: ['source', 'channel', 'campaign', 'country', 'device'],
  },
];

export const METRIC_CATALOG: readonly MetricDefinition[] = [
  ...PERFORMANCE_METRICS,
  ...TRAFFIC_METRICS,
  ...BEHAVIOUR_METRICS,
  ...SALES_METRICS,
];

// ─── Lookups ──────────────────────────────────────────────────────────────────

const CATALOG_BY_ID: Record<string, MetricDefinition> = Object.fromEntries(
  METRIC_CATALOG.map((m) => [m.id, m]),
);

export function getMetric(id: string): MetricDefinition | undefined {
  return CATALOG_BY_ID[id];
}

export function metricsForDomain(domain: MetricDomain): MetricDefinition[] {
  return METRIC_CATALOG.filter((m) => m.domain === domain);
}

/** Metrics the resolver can actually query right now (pipe-backed). */
export function queryableMetrics(): MetricDefinition[] {
  return METRIC_CATALOG.filter((m) => !m.comingSoon);
}

// ─── Query spec (the internal representation) ──────────────────────────────────
//
// This is what the assistant emits and the resolver consumes. Users
// never author it directly — they describe what they want and the
// assistant fills this in. Deliberately small + serialisable so it can
// be persisted as a space card (`space_cards.config` jsonb) and
// round-tripped through a URL.

export interface MetricQuerySpec {
  /** Catalog metric id. Must resolve via `getMetric`. */
  metricId: string;
  /** Aggregation override; falls back to the metric's default. */
  aggregation?: Aggregation;
  /** Equality filters keyed by dimension — e.g. `{ device: 'phone',
   *  country: 'GB' }`. The resolver maps each to the real column. */
  filters?: Partial<Record<MetricDimension, string>>;
  /** When set, the result is a breakdown (one row per bucket of this
   *  dimension) rather than a single time series. */
  groupBy?: MetricDimension;
  /** Resolve this query against a specific site, overriding the card's
   *  site. Lets one card compare the same metric across sites (site
   *  comparison). Must be a site the caller owns; defaults to the
   *  card's site when unset. */
  siteUuid?: string;
}

/** A single rendered card on a dashboard / the explore canvas. One
 *  card can overlay multiple specs (e.g. visitors + sessions). */
export interface DashboardCardSpec {
  id: string;
  title: string;
  /** How the card draws — the renderer picks the component.
   *    line      — series over time (multi-series overlay for comparison)
   *    area      — filled, band-coloured trend (single series)
   *    bar       — vertical bars (breakdown buckets, or grouped compare)
   *    number    — one headline value
   *    stat      — headline value + sparkline + period delta
   *    breakdown — horizontal bars, one row per bucket
   *    ranking   — breakdown sorted worst/biggest first
   *    table     — rows × columns (buckets or compared metrics/sites)
   *    compare   — scorecard grid: one row per query (metric), columns
   *                for the card's site vs `compareSiteUuid` + a delta */
  viz: 'line' | 'area' | 'bar' | 'number' | 'stat' | 'breakdown' | 'ranking' | 'table' | 'compare';
  queries: MetricQuerySpec[];
  /** Grid span (1–12) so AI-built dashboards can size cards. Layout on
   *  a persisted space is authoritative via the space_card row's grid
   *  coords; this is the fallback width for an unplaced/ephemeral card. */
  span?: number;
  /** Which site this card reads from. Per-site spaces: a card belongs
   *  to its space's site; the API forces this to match on create. */
  siteUuid?: string;
  /** Site-comparison overlay: when set, the renderer resolves every
   *  query twice — once against the card's site, once against this
   *  guest site — and draws both (paired columns for `compare`,
   *  overlaid series for trend vizes). Queries stay ≤6 per card, so
   *  the doubled resolve stays within /v1/metrics/query's 12-query
   *  cap. Must be a site the caller owns (validated at resolve). */
  compareSiteUuid?: string;
}

/** A pinned/AI-built dashboard: an ordered set of cards + the window
 *  they share. The whole thing serialises to one jsonb blob. */
export interface DashboardSpec {
  /** Look-back window in days. Shared by every card unless a card's
   *  spec overrides it later. */
  rangeDays: number;
  cards: DashboardCardSpec[];
}

// ─── Validation ────────────────────────────────────────────────────────────────

/** True when a spec references a real, queryable (non-comingSoon)
 *  metric with a valid aggregation + dimensions. The assistant's
 *  output is run through this before it ever hits the resolver. */
export function isQueryableSpec(spec: MetricQuerySpec): boolean {
  const metric = getMetric(spec.metricId);
  if (!metric || metric.comingSoon) return false;
  if (spec.aggregation && !metric.allowedAggregations.includes(spec.aggregation)) return false;
  // Breakdown is only valid for a dimension the resolver actually backs
  // (groupByDimensions ⊆ dimensions). This is what stops the assistant
  // emitting e.g. "bounce rate by source" — advertised as filterable
  // but not group-able — which the resolver would reject.
  if (spec.groupBy && !metric.groupByDimensions.includes(spec.groupBy)) return false;
  if (spec.filters) {
    for (const dim of Object.keys(spec.filters) as MetricDimension[]) {
      if (!metric.dimensions.includes(dim)) return false;
    }
  }
  return true;
}

// ─── Definition builders (keep the registry above terse) ────────────────────────
//
// NOTE: the builder *functions* below are hoisted, but any `const`
// they close over (e.g. RUM_PERF_DIMENSIONS) is NOT — it lives above
// the registry so it's initialised before the registry arrays run at
// module load. Don't move shared consts back down here or the
// registry hits a temporal-dead-zone ReferenceError.

function rumCwv(
  id: string,
  label: string,
  description: string,
  unit: CatalogMetricUnit,
  thresholds: { good: number; poor: number },
): MetricDefinition {
  return {
    id,
    label,
    description,
    domain: 'performance',
    source: 'rum_perf',
    unit,
    lowerIsBetter: true,
    defaultAggregation: 'p75',
    allowedAggregations: ['p50', 'p75', 'p95', 'p99'],
    dimensions: RUM_PERF_DIMENSIONS,
    // The breakdown MV only carries CWV by country — the resolver backs
    // groupBy:'country' and rejects the rest.
    groupByDimensions: ['country'],
    thresholds,
  };
}

function synthetic(
  id: string,
  label: string,
  description: string,
  unit: CatalogMetricUnit,
  thresholds: { good: number; poor: number },
): MetricDefinition {
  return {
    id,
    label,
    description,
    domain: 'performance',
    source: 'synthetic',
    unit,
    lowerIsBetter: true,
    defaultAggregation: 'p75',
    allowedAggregations: ['p50', 'p75', 'p95', 'p99', 'avg'],
    dimensions: ['device', 'region', 'page'],
    // Synthetic resolves to a time series (or single number) via the
    // explore-aggregated client — no per-bucket breakdown pipe.
    groupByDimensions: [],
    thresholds,
  };
}

function syntheticScore(id: string, label: string, description: string): MetricDefinition {
  return {
    id,
    label,
    description,
    domain: 'performance',
    source: 'synthetic',
    unit: 'score',
    // Lighthouse scores are higher-is-better (90+ good, <50 poor).
    lowerIsBetter: false,
    defaultAggregation: 'p75',
    allowedAggregations: ['p50', 'p75', 'p95', 'avg'],
    dimensions: ['device', 'region', 'page'],
    groupByDimensions: [],
    thresholds: { good: 90, poor: 50 },
  };
}

function trafficCount(id: string, label: string, description: string): MetricDefinition {
  return {
    id,
    label,
    description,
    domain: 'traffic',
    source: 'rum_traffic',
    unit: 'count',
    lowerIsBetter: false,
    defaultAggregation: 'sum',
    allowedAggregations: ['sum'],
    dimensions: ['device', 'country', 'region', 'browser', 'os', 'channel', 'source', 'page'],
    // The generic breakdown pipe carries the magnitude metrics by every
    // dimension that has a column — all of the above except `page`,
    // which the breakdown pipe doesn't key on.
    groupByDimensions: ['device', 'country', 'region', 'browser', 'os', 'channel', 'source'],
  };
}
